import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/heroSlides";
import { getMovieById, getCategoryName } from "@/utils/movie";
import SearchBox from "@/components/feature/SearchBox";

const slides = heroSlides
  .map((slide) => {
    const movie = getMovieById(slide.movieId);
    return movie ? { ...slide, movie } : null;
  })
  .filter((slide): slide is NonNullable<typeof slide> => slide !== null);

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((value) => (value + 1) % slides.length);
    }, 6500);
    return () => clearInterval(timer);
  }, []);

  if (slides.length === 0) return null;
  const current = slides[index];

  return (
    <section className="relative w-full overflow-hidden bg-background-50">
      <div className="relative h-[560px] w-full md:h-[640px]">
        <div key={current.movie.id} className="absolute inset-0 animate-fade-in">
          <img
            src={current.movie.cover}
            alt={`${current.movie.title} 影片剧照`}
            title={`${current.movie.title} - 黄色大片 热门电影`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/55 to-background-50/35" />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50/85 via-background-50/30 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col px-4 pb-14 pt-10 md:px-6 md:pb-16 md:pt-14">
          <h1 className="animate-fade-up font-heading text-2xl font-bold leading-tight text-foreground-50 md:text-3xl">
            黄色大片
            <span className="mt-2 block font-sans text-sm font-normal tracking-wide text-foreground-200 md:text-base">
              最新热门电影资讯与推荐 · 为影迷甄选每一部值得看的电影
            </span>
          </h1>

          <div className="mt-auto max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 font-label text-xs uppercase tracking-widest text-foreground-950">
              <i className="ri-fire-fill text-sm" />
              {current.label}
            </span>
            <h2 className="mt-4 font-heading text-3xl font-black leading-tight text-foreground-50 md:text-5xl">
              {current.movie.title}
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-200 md:text-base">
              {current.tagline}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-300 md:text-sm">
              <span className="flex items-center gap-1 font-semibold text-primary-400">
                <i className="ri-star-fill" />
                {current.movie.rating.toFixed(1)}
              </span>
              <span>{current.movie.year}</span>
              <span>{current.movie.region}</span>
              <span>{current.movie.genres.map((genre) => getCategoryName(genre)).join(" / ")}</span>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.movie.id}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 font-label text-sm uppercase tracking-wide text-foreground-950 transition-colors hover:bg-primary-400"
              >
                <i className="ri-play-circle-line text-lg" />
                查看详情
              </Link>
              <Link
                to="/special"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-foreground-50/30 bg-background-50/50 px-6 py-3 font-label text-sm uppercase tracking-wide text-foreground-50 backdrop-blur-sm transition-colors hover:border-primary-400 hover:text-primary-400"
              >
                <i className="ri-article-line text-lg" />
                影评专题
              </Link>
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between gap-4">
            <div className="hidden w-full max-w-md md:block">
              <SearchBox size="md" placeholder="搜索想看的电影、导演或演员…" />
            </div>
            <div className="ml-auto flex items-center gap-2">
              {slides.map((slide, dotIndex) => (
                <button
                  key={slide.movie.id}
                  type="button"
                  aria-label={`切换到第 ${dotIndex + 1} 张`}
                  onClick={() => setIndex(dotIndex)}
                  className={`h-1.5 rounded-full transition-all ${
                    dotIndex === index ? "w-8 bg-primary-500" : "w-4 bg-foreground-50/40 hover:bg-foreground-50/70"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}